<!--
 * @Description: 流向管理-上传流向（发票识别 / 手动填写）
 * 多张发票按发票分组展示，每张发票有独立的开票日期、购买方信息和明细
-->
<template>
  <div class="flow-page">
    <div class="nav-bar">
      <div class="nav-back" @click="onBack">
        <svg viewBox="0 0 24 24" width="20" height="20">
          <path d="M15 5L8 12l7 7" fill="none" stroke="#333" stroke-width="2" stroke-linecap="round"
            stroke-linejoin="round" />
        </svg>
      </div>
      <!-- <div class="nav-title">上传流向</div> -->
      <div class="nav-right"></div>
    </div>

    <div class="page-body">
      <!-- 流向数据选择 -->
      <template v-if="step === 'entry'">
        <!-- <div class="section-title">流向数据</div> -->
        <div class="option-card" @click="chooseInvoice">
          <div class="option-icon">
            <svg viewBox="0 0 24 24" width="24" height="24">
              <path d="M4 7h3l1.5-2h7L17 7h3a1 1 0 0 1 1 1v11a1 1 0 0 1-1 1H4a1 1 0 0 1-1-1V8a1 1 0 0 1 1-1z"
                fill="none" stroke="#fff" stroke-width="2" />
              <circle cx="12" cy="13" r="3.6" fill="none" stroke="#fff" stroke-width="2" />
            </svg>
          </div>
          <div class="option-text">发票识别</div>
        </div>
        <div class="option-card" @click="chooseManual">
          <div class="option-icon">
            <svg viewBox="0 0 24 24" width="24" height="24">
              <path d="M4 20h4l10.5-10.5a1 1 0 0 0 0-1.4l-2.6-2.6a1 1 0 0 0-1.4 0L4 16v4z" fill="none" stroke="#fff"
                stroke-width="2" />
              <path d="M13.5 6.5l4 4" fill="none" stroke="#fff" stroke-width="2" />
            </svg>
          </div>
          <div class="option-text">手动填写</div>
        </div>
        <input ref="invoiceInput" class="hidden-input" type="file" accept="image/*,application/pdf" multiple
          @change="onInvoiceChange" />
      </template>

      <!-- 上传流向表单 -->
      <template v-else>
        <div class="form-title">请确认流向数据：</div>

        <!-- 按发票分组 -->
        <div v-for="(invoice, invIdx) in formData.invoiceContents" :key="invIdx" class="invoice-group">
          <div v-if="formData.invoiceContents.length > 1" class="invoice-head">
            <span class="invoice-index">发票 {{ invIdx + 1 }}</span>
            <span class="invoice-remove" @click="removeInvoice(invIdx)">删除</span>
          </div>

          <div class="field">
            <div class="field-label">开票日期</div>
            <div class="field-control field-picker" @click="openDatePicker(invIdx)">
              <span :class="invoice.invoiceDate ? '' : 'placeholder'">
                {{ invoice.invoiceDate || '请选择开票日期' }}
              </span>
              <svg class="field-arrow" viewBox="0 0 24 24">
                <path d="M6 9l6 6 6-6" fill="none" stroke="#C8C9CC" stroke-width="2" />
              </svg>
            </div>
          </div>

          <div class="field">
            <div class="field-label">购买方信息</div>
            <div class="field-control">
              <input v-model.trim="invoice.buyerName" type="text" maxlength="100" placeholder="请输入购买方信息" />
            </div>
          </div>

          <div v-if="invoice.unmatchedProducts && invoice.unmatchedProducts.length" class="unmatched-tip">
            <div class="unmatched-title">以下产品未匹配到，请手动选择：</div>
            <div v-for="(p, idx) in invoice.unmatchedProducts" :key="idx" class="unmatched-item">
              · {{ p.productName || '未命名产品' }}{{ p.specify ? ` / ${p.specify}` : '' }}
            </div>
          </div>

          <!-- 货物明细 -->
          <div class="product-list">
            <div class="list-header">
              <span class="list-title">货物明细</span>
              <span class="list-count">共 {{ invoice.items.length }} 条</span>
            </div>

            <div v-for="(item, itemIdx) in invoice.items" :key="itemIdx" class="product-card">
              <div class="card-head">
                <span class="card-index">明细 {{ itemIdx + 1 }}</span>
                <span v-if="invoice.items.length > 1" class="card-remove" @click="removeItem(invIdx, itemIdx)">
                  <svg viewBox="0 0 24 24" width="16" height="16">
                    <path d="M18 6L6 18M6 6l12 12" stroke="#999" stroke-width="2" stroke-linecap="round" />
                  </svg>
                </span>
              </div>

              <div class="field">
                <div class="field-label">项目名称</div>
                <div class="field-control field-picker" @click="openItemPicker(invIdx, itemIdx, 'project')">
                  <span :class="item.productName ? '' : 'placeholder'">
                    {{ item.productName || '请选择项目名称' }}
                  </span>
                  <svg class="field-arrow" viewBox="0 0 24 24">
                    <path d="M6 9l6 6 6-6" fill="none" stroke="#C8C9CC" stroke-width="2" />
                  </svg>
                </div>
              </div>

              <div class="field">
                <div class="field-label">规格型号</div>
                <div class="field-control field-picker" @click="openItemPicker(invIdx, itemIdx, 'specify')">
                  <span :class="item.specify ? '' : 'placeholder'">
                    {{ item.specify || '请选择规格型号' }}
                  </span>
                  <svg class="field-arrow" viewBox="0 0 24 24">
                    <path d="M6 9l6 6 6-6" fill="none" stroke="#C8C9CC" stroke-width="2" />
                  </svg>
                </div>
              </div>

              <div class="field">
                <div class="field-label">单位</div>
                <div class="field-control field-picker" @click="openItemPicker(invIdx, itemIdx, 'unit')">
                  <span :class="item.unit ? '' : 'placeholder'">
                    {{ item.unit || '请选择单位' }}
                  </span>
                  <svg class="field-arrow" viewBox="0 0 24 24">
                    <path d="M6 9l6 6 6-6" fill="none" stroke="#C8C9CC" stroke-width="2" />
                  </svg>
                </div>
              </div>

              <div class="field">
                <div class="field-label">数量</div>
                <div class="field-control">
                  <input v-model.trim="item.quantity" type="number" inputmode="decimal" placeholder="请输入数量" />
                </div>
              </div>
            </div>

            <div class="add-item" @click="addItem(invIdx)">
              <svg viewBox="0 0 24 24" width="18" height="18">
                <path d="M12 5v14M5 12h14" stroke="#f9ad30" stroke-width="2" stroke-linecap="round" />
              </svg>
              <span>添加明细</span>
            </div>
          </div>
        </div>

        <div class="field upload-field-wrap">
          <div class="field-label">流向附件</div>
          <div class="field-control upload-field">
            <van-uploader v-model="fileList" accept="image/*,application/pdf" multiple :max-count="10"
              :preview-size="72" :after-read="afterRead" />
          </div>
        </div>
      </template>
    </div>

    <div v-if="step === 'form'" class="bottom-bar">
      <button class="confirm-btn" :disabled="submitting" @click="onConfirm">
        {{ submitting ? '提交中...' : '确认' }}
      </button>
    </div>

    <van-popup v-model="showDatePicker" position="bottom" round>
      <van-datetime-picker v-model="pickerDate" title="选择开票日期" type="date" :min-date="minDate" :max-date="maxDate"
        @confirm="onDateConfirm" @cancel="showDatePicker = false" />
    </van-popup>

    <van-popup v-model="showPicker" position="bottom" round>
      <van-picker show-toolbar :title="pickerTitle" :columns="pickerColumns" @confirm="onPickerConfirm"
        @cancel="showPicker = false" />
    </van-popup>
  </div>
</template>

<script>
import restApi from '@/api/restapi';
import { getProductDict, uploadFiles, recognizeInvoices, selectProductByNameAndSpecify, submitHandFlow, submitOcrFlow } from '@/api/flowApi';

const pad = (n) => String(n).padStart(2, '0');
const formatDotDate = (date) => `${date.getFullYear()}.${pad(date.getMonth() + 1)}.${pad(date.getDate())}`;

const isImageUrl = (url = '') => {
  const path = String(url).split('?')[0].toLowerCase();
  return !path.endsWith('.pdf');
};

const normalizeProduct = (product) => ({
  id: product.id ?? product.productId ?? '',
  name: (product.name || product.productName || '').trim(),
  specify: (product.specify || product.specification || product.spec || product.model || '').trim(),
  baseUnit: product.baseUnit || '',
  assistedUnit: product.assistedUnit || '',
  exchangeValue: product.exchangeValue,
  price: product.price || 0,
});

const emptyItem = () => ({
  productId: '',
  productName: '',
  baseUnit: '',
  assistedUnit: '',
  exchangeValue: undefined,
  specify: '',
  unit: '',
  quantity: '',
  price: 0,
  selectedProduct: null,
});

const emptyInvoice = () => ({
  invoiceDate: '',
  buyerName: '',
  invoiceUrl: '',
  items: [emptyItem()],
  unmatchedProducts: [],
});

const emptyForm = () => ({
  invoiceContents: [emptyInvoice()],
});

export default {
  name: 'FlowUpload',
  data() {
    return {
      step: 'entry',
      submitting: false,
      entryType: 'manual', // manual | ocr
      formData: emptyForm(),
      products: [],
      productReady: Promise.resolve([]),
      fileList: [],
      showDatePicker: false,
      pickerDate: new Date(),
      dateCtx: 0,
      minDate: new Date(2000, 0, 1),
      maxDate: new Date(),
      showPicker: false,
      pickerTitle: '',
      pickerColumns: [],
      pickerCtx: { invIdx: -1, itemIdx: -1, type: '' },
    };
  },
  computed: {
    chatId() {
      return restApi.user.id || `flow_${Date.now()}`;
    },
    projectOptions() {
      return [...new Set(this.products.map((item) => item.name).filter(Boolean))];
    },
  },
  mounted() {
    document.title = '上传流向';
    this.loadProducts();
    console.log("window.wx", window.wx, window.wx.miniProgram)
  },
  methods: {
    onBack() {
      if (this.step === 'form') {
        this.step = 'entry';
        return;
      }
      this.$router.back();
    },
    loadProducts() {
      this.productReady = getProductDict()
        .then((list) => {
          this.products = list.map(normalizeProduct).filter((item) => item.name);
          return this.products;
        })
        .catch(() => {
          this.$toast('产品列表加载失败');
          return [];
        });
      return this.productReady;
    },
    chooseManual() {
      this.entryType = 'manual';
      this.resetForm();
      this.step = 'form';
    },
    chooseInvoice() {
      this.$refs.invoiceInput && this.$refs.invoiceInput.click();
    },
    async onInvoiceChange(event) {
      const files = Array.from(event.target.files || []);
      event.target.value = '';
      if (!files.length) return;

      const invalid = files.find((file) => {
        const isPdf = file.type === 'application/pdf' || /\.pdf$/i.test(file.name);
        return !(file.type.indexOf('image/') === 0 || isPdf);
      });
      if (invalid) {
        this.$toast('只支持上传图片或PDF文件');
        return;
      }
      let errMsg = '';
      this.$toast.loading({ message: '正在识别发票...', duration: 0, forbidClick: true });
      try {
        const urls = await uploadFiles(files);
        // 一次 SSE 连接逐个识别多个文件，全部完成后断开
        const texts = await recognizeInvoices({ urls, chatId: this.chatId });
        // 每张发票（每个文件）对应一个分组，保留各自的开票日期和购买方信息
        const groups = [];
        urls.forEach((url, i) => {
          const text = texts[i];
          if (!text) return;
          const result = JSON.parse(text);

          const list = result && result.invoiceContents
            ? result.invoiceContents
            : (result ? [result] : []);
          list.forEach((c) => {
            if (!c) return;
            groups.push({ ...c, invoiceUrl: c.invoiceUrl || url });
          });
        });
        if (!groups.length) {
          throw new Error('未识别到发票数据，请核对发票文件后重试');
        }
        // 把识别到的数据发给后端校验，用返回的正确数据回填表单
        const checked = await this.checkRecognizedData(groups);
        await this.fillFromInvoices(checked, urls);
        this.entryType = 'ocr';
        this.step = 'form';
      } catch (error) {
        errMsg = error.message || '发票识别失败，请重试';
      } finally {
        this.$toast.clear();
        if (errMsg) this.$toast(errMsg);
      }
    },
    // 把识别到的发票数据发给后端确认，返回校正后的 invoiceContents
    async checkRecognizedData(groups) {
      const cmId = this.$route.query && (this.$route.query.cmId || this.$route.query.cmid)
        ? String(this.$route.query.cmId || this.$route.query.cmid)
        : '';
      const payload = {
        cmId: Number(cmId) || 0,
        invoiceContents: groups.map((g) => ({
          buyName: g.buyName || '',
          invoiceDate: (g.invoiceDate || '').replace(/\./g, '-'),
          invoiceUrl: g.invoiceUrl || '',
          data: (Array.isArray(g.data) ? g.data : []).map((row) => ({
            productId: row.productId || 0,
            productName: (row.productName || '').trim(),
            baseUnit: row.baseUnit || '',
            assistedUnit: row.assistedUnit || '',
            exchangeValue: Number(row.exchangeValue) || 0,
            specify: (row.specify || '').trim(),
            quantity: Number(row.quantity) || 0,
            unit: row.unit || '',
            price: Number(row.price) || 0,
            flowManagerStatus: row.flowManagerStatus || 0,
          })),
        })),
      };
      const checked = await selectProductByNameAndSpecify(payload);
      const contents = checked && Array.isArray(checked.invoiceContents)
        ? checked.invoiceContents
        : null;
      if (!contents || !contents.length) return null;
      return contents.map((c) => ({
        ...c,
        invoiceDate: (c.invoiceDate || '').replace(/-/g, '.'),
      }));
    },
    // 按发票分组回填：每张发票保留自己的开票日期 / 购买方信息 / 明细
    // 单条明细是否匹配到有效产品：productId 必须有效，且 flowManagerStatus != 0
    isMatchedRow(row) {
      const pid = row && row.productId;
      if (!pid || Number(pid) === 0) return false;
      const status = row && row.flowManagerStatus;
      if (status !== undefined && status !== null && Number(status) === 0) return false;
      return true;
    },
    async fillFromInvoices(contents, urls) {
      await this.productReady;
      this.formData = {
        invoiceContents: contents.map((content) => {
          const rows = Array.isArray(content.data) ? content.data : [];
          const items = [];
          const unmatchedProducts = [];
          rows.forEach((row) => {
            const productName = (row.productName || '').trim();
            const specify = (row.specify || '').trim();
            if (!this.isMatchedRow(row)) {
              // 未匹配：保留原始名称/规格供用户参考
              unmatchedProducts.push({ productName, specify });
              return;
            }
            const matched = this.matchProduct(productName, specify);
            items.push({
              productId: row.productId || (matched ? matched.id : ''),
              productName: productName || (matched ? matched.name : ''),
              baseUnit: row.baseUnit || (matched ? matched.baseUnit : ''),
              assistedUnit: row.assistedUnit || (matched ? matched.assistedUnit : ''),
              exchangeValue: row.exchangeValue !== undefined ? row.exchangeValue : (matched ? matched.exchangeValue : undefined),
              specify: specify || (matched ? matched.specify : ''),
              unit: row.unit || (matched ? matched.baseUnit || matched.assistedUnit : ''),
              quantity: row.quantity !== undefined && row.quantity !== null ? String(row.quantity) : '',
              price: row.price || 0,
              selectedProduct: matched,
            });
          });
          return {
            invoiceDate: content.invoiceDate || '',
            buyerName: (content.buyName || '').trim(),
            invoiceUrl: content.invoiceUrl || urls.join(','),
            items: items.length ? items : [emptyItem()],
            unmatchedProducts,
          };
        }),
      };
      this.fileList = urls.map((url) => ({
        url,
        isImage: isImageUrl(url),
        __serverUrl: url,
      }));
    },
    matchProduct(name, specify) {
      if (!name) return null;
      const sameName = this.products.filter((item) => item.name === name);
      if (!sameName.length) return null;
      if (specify) return sameName.find((item) => item.specify === specify) || sameName[0];
      return sameName[0];
    },
    // --- 明细操作 ---
    addItem(invIdx) {
      this.formData.invoiceContents[invIdx].items.push(emptyItem());
    },
    removeItem(invIdx, itemIdx) {
      this.formData.invoiceContents[invIdx].items.splice(itemIdx, 1);
    },
    // 删除整张发票（多于 1 张时可删），同时移除其对应附件
    removeInvoice(invIdx) {
      const list = this.formData.invoiceContents;
      if (list.length <= 1) return;
      const target = list[invIdx];
      list.splice(invIdx, 1);
      const urls = (target.invoiceUrl || '').split(',').filter(Boolean);
      if (urls.length) {
        this.fileList = this.fileList.filter((f) => !urls.includes(f.__serverUrl || f.url));
      }
    },
    getItemSpecifyOptions(invIdx, itemIdx) {
      const item = this.formData.invoiceContents[invIdx].items[itemIdx];
      if (!item.productName) return [];
      const list = this.products
        .filter((p) => p.name === item.productName)
        .map((p) => p.specify)
        .filter(Boolean);
      const options = [...new Set(list)];
      if (item.specify && !options.includes(item.specify)) options.push(item.specify);
      return options;
    },
    getItemUnitOptions(invIdx, itemIdx) {
      const item = this.formData.invoiceContents[invIdx].items[itemIdx];
      const options = [];
      if (item.selectedProduct) {
        if (item.selectedProduct.baseUnit) options.push(item.selectedProduct.baseUnit);
        if (item.selectedProduct.assistedUnit) options.push(item.selectedProduct.assistedUnit);
      }
      if (item.unit && !options.includes(item.unit)) options.push(item.unit);
      return [...new Set(options)];
    },
    // --- 日期 & 选择器 ---
    openDatePicker(invIdx) {
      this.dateCtx = invIdx;
      const parts = (this.formData.invoiceContents[invIdx].invoiceDate || '').split('.');
      this.pickerDate = parts.length === 3
        ? new Date(Number(parts[0]), Number(parts[1]) - 1, Number(parts[2]))
        : new Date();
      this.showDatePicker = true;
    },
    onDateConfirm(date) {
      this.formData.invoiceContents[this.dateCtx].invoiceDate = formatDotDate(date);
      this.showDatePicker = false;
    },
    openItemPicker(invIdx, itemIdx, type) {
      let columns = [];
      if (type === 'project') {
        columns = this.projectOptions;
        this.pickerTitle = '项目名称';
      } else if (type === 'specify') {
        columns = this.getItemSpecifyOptions(invIdx, itemIdx);
        this.pickerTitle = '规格型号';
      } else {
        columns = this.getItemUnitOptions(invIdx, itemIdx);
        this.pickerTitle = '单位';
      }
      if (!columns.length) {
        this.$toast('暂无可选项');
        return;
      }
      this.pickerCtx = { invIdx, itemIdx, type };
      this.pickerColumns = columns;
      this.showPicker = true;
    },
    onPickerConfirm(value) {
      const { invIdx, itemIdx, type } = this.pickerCtx;
      const item = this.formData.invoiceContents[invIdx]
        && this.formData.invoiceContents[invIdx].items[itemIdx];
      if (!item) { this.showPicker = false; return; }
      if (type === 'project') {
        item.productName = value;
        item.specify = '';
        item.unit = '';
        item.selectedProduct = null;
        item.productId = '';
        item.baseUnit = '';
        item.assistedUnit = '';
        item.exchangeValue = undefined;
      } else if (type === 'specify') {
        item.specify = value;
        const matched = this.matchProduct(item.productName, value);
        item.selectedProduct = matched;
        if (matched) {
          item.productId = matched.id;
          item.baseUnit = matched.baseUnit;
          item.assistedUnit = matched.assistedUnit;
          item.exchangeValue = matched.exchangeValue;
          if (!item.unit) item.unit = matched.baseUnit || matched.assistedUnit || '';
        }
      } else {
        item.unit = value;
      }
      this.showPicker = false;
    },
    // --- 附件上传 ---
    async afterRead(file) {
      const list = Array.isArray(file) ? file : [file];
      await Promise.all(list.map((item) => this.uploadOne(item)));
    },
    async uploadOne(item) {
      if (item.__serverUrl) return;
      item.status = 'uploading';
      item.message = '上传中';
      try {
        const urls = await uploadFiles(item.file);
        this.$set(item, '__serverUrl', urls[0]);
        item.url = urls[0];
        item.status = 'done';
        item.message = '';
      } catch (error) {
        item.status = 'failed';
        item.message = '上传失败';
        this.$toast(error.message || '附件上传失败');
      }
    },
    attachmentUrls() {
      return this.fileList.map((item) => item.__serverUrl).filter(Boolean);
    },
    // --- 校验 & 提交 ---
    validateForm() {
      const contents = this.formData.invoiceContents;
      if (!contents.length) return '请添加发票数据';
      const multi = contents.length > 1;
      for (let i = 0; i < contents.length; i++) {
        const inv = contents[i];
        const prefix = multi ? `发票${i + 1}：` : '';
        if (!inv.invoiceDate) return `请选择${prefix}开票日期`;
        if (!inv.buyerName) return `请输入${prefix}购买方信息`;
        if (!inv.items.length) return `请添加${prefix}货物明细`;
        for (let j = 0; j < inv.items.length; j++) {
          const it = inv.items[j];
          const itemPrefix = `${prefix}第${j + 1}条：`;
          if (!it.productName) return `${itemPrefix}请选择项目名称`;
          if (!it.specify) return `${itemPrefix}请选择规格型号`;
          if (!it.unit) return `${itemPrefix}请选择单位`;
          const q = Number(it.quantity);
          if (!it.quantity || Number.isNaN(q) || q <= 0) return `${itemPrefix}请输入正确的数量`;
        }
      }
      if (this.fileList.some((f) => f.status === 'uploading')) return '附件上传中，请稍候';
      if (!this.attachmentUrls().length) return '请上传流向附件';
      return '';
    },
    async onConfirm() {
      if (this.submitting) return;
      const errMsg = this.validateForm();
      if (errMsg) {
        this.$toast(errMsg);
        return;
      }

      const cmId = this.$route.query && (this.$route.query.cmId || this.$route.query.cmid)
        ? String(this.$route.query.cmId || this.$route.query.cmid)
        : '';
      const allAttachmentUrls = this.attachmentUrls().join(',');

      const invoiceContents = this.formData.invoiceContents.map((inv) => ({
        buyName: inv.buyerName,
        invoiceDate: inv.invoiceDate.replace(/\./g, '-'),
        invoiceUrl: inv.invoiceUrl || allAttachmentUrls,
        data: inv.items.map((it) => ({
          productId: it.selectedProduct ? it.selectedProduct.id : (it.productId || 0),
          productName: it.productName,
          baseUnit: it.selectedProduct ? it.selectedProduct.baseUnit : it.baseUnit,
          assistedUnit: it.selectedProduct ? it.selectedProduct.assistedUnit : it.assistedUnit,
          exchangeValue: it.selectedProduct ? it.selectedProduct.exchangeValue : it.exchangeValue,
          specify: it.specify,
          quantity: Number(it.quantity),
          unit: it.unit,
          price: Number(it.price) || 0,
          flowManagerStatus: 0,
        })),
      }));

      const submitData = { cmId: Number(cmId) || 0, invoiceContents };

      this.submitting = true;
      try {
        const { data } = this.entryType === 'ocr'
          ? await submitOcrFlow(submitData)
          : await submitHandFlow(submitData);
        console.log("data", data)
        if (data && data.success) {
          this.$toast.success('提交成功');
          this.resetForm();
          this.step = 'entry';
          this.backToMiniProgram();
        } else {
          this.$toast((data && data.message) || '提交失败');
        }
      } catch (error) {
        this.$toast('提交失败，请重试');
      } finally {
        this.submitting = false;
      }
    },
    resetForm() {
      this.formData = emptyForm();
      this.fileList = [];
    },
    backToMiniProgram() {
      console.log("window.wx", window.wx, window.wx.miniProgram)
      const navTo = () => {
        console.log("navigateTo",)
        window.wx.miniProgram.redirectTo({
          url: '/packageChannel/pages/channelFlowManager/index',
        });
      };
      if (window.wx && window.wx.miniProgram) {
        window.wx.ready ? navTo() : '';
      } else {
        this.step = 'entry';
      }
    },
  },
};
</script>

<style scoped>
.flow-page {
  min-height: 100vh;
  background: #ffffff;
}

.nav-bar {
  position: sticky;
  top: 0;
  z-index: 10;
  display: grid;
  grid-template-columns: 46px 1fr 46px;
  align-items: center;
  height: 46px;
  background: #ffffff;
}

.nav-back {
  display: flex;
  align-items: center;
  justify-content: center;
  height: 46px;
  cursor: pointer;
}

.nav-title {
  text-align: center;
  font-size: 17px;
  font-weight: 500;
  color: #333333;
}

.page-body {
  padding: 20px 20px 100px;
}

.section-title {
  margin-bottom: 14px;
  font-size: 15px;
  font-weight: 500;
  color: #333333;
}

.option-card {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 120px;
  margin-bottom: 18px;
  background: #fff9ee;
  border: 1px dashed #fbb13d;
  border-radius: 8px;
  cursor: pointer;
}

.option-icon {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 44px;
  height: 44px;
  margin-bottom: 10px;
  background: #fbb13d;
  border-radius: 50%;
}

.option-text {
  font-size: 15px;
  color: #333333;
}

.hidden-input {
  display: none;
}

.form-title {
  margin-bottom: 18px;
  font-size: 15px;
  font-weight: 600;
  color: #333333;
}

/* 发票分组 */
.invoice-group {
  margin-bottom: 8px;
}

.invoice-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 12px;
  padding-top: 4px;
  border-top: 1px dashed #ebedf0;
}

.invoice-remove {
  font-size: 12px;
  color: #f5222d;
}

.invoice-group:first-child .invoice-head {
  border-top: 0;
}

.invoice-index {
  padding: 2px 10px;
  background: #fff3dd;
  border-radius: 10px;
  font-size: 12px;
  font-weight: 600;
  color: #f9ad30;
}

.field {
  margin-bottom: 16px;
}

.field-label {
  margin-bottom: 8px;
  font-size: 14px;
  color: #333333;
  line-height: 20px;
}

.field-control {
  position: relative;
  display: flex;
  align-items: center;
  height: 46px;
  padding: 0 12px;
  background: #f7f8fa;
  border-radius: 8px;
  font-size: 14px;
  color: #333333;
}

.field-control input {
  width: 100%;
  height: 100%;
  padding: 0;
  background: transparent;
  border: 0;
  outline: none;
  font-family: inherit;
  font-size: 14px;
  color: #333333;
}

.field-control input::placeholder {
  color: #c8c9cc;
}

.field-control input[type=number]::-webkit-inner-spin-button,
.field-control input[type=number]::-webkit-outer-spin-button {
  margin: 0;
  -webkit-appearance: none;
}

.field-picker {
  justify-content: space-between;
  padding-right: 36px;
  cursor: pointer;
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
}

.field-picker>span {
  overflow: hidden;
  text-overflow: ellipsis;
}

.placeholder {
  color: #c8c9cc;
}

.unmatched-tip {
  margin-bottom: 16px;
  padding: 10px 12px;
  background: #fff2f0;
  border: 1px solid #ffccc7;
  border-radius: 6px;
}

.unmatched-title {
  font-size: 12px;
  font-weight: 600;
  color: #f5222d;
  margin-bottom: 4px;
}

.unmatched-item {
  font-size: 12px;
  color: #f5222d;
  line-height: 1.6;
}

.field-arrow {
  position: absolute;
  right: 12px;
  width: 16px;
  height: 16px;
}

/* 明细列表 */
.product-list {
  margin-bottom: 8px;
}

.list-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 12px;
}

.list-title {
  font-size: 14px;
  font-weight: 600;
  color: #333333;
}

.list-count {
  font-size: 12px;
  color: #999999;
}

.product-card {
  position: relative;
  padding: 16px;
  margin-bottom: 12px;
  background: #fff9ee;
  border: 1px solid #fbb13d;
  border-radius: 8px;
}

.card-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 12px;
}

.card-index {
  font-size: 13px;
  font-weight: 600;
  color: #f9ad30;
}

.card-remove {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  cursor: pointer;
}

.card-remove:active {
  opacity: 0.6;
}

.add-item {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  height: 44px;
  background: #ffffff;
  border: 1px dashed #f9ad30;
  border-radius: 8px;
  font-size: 14px;
  color: #f9ad30;
  cursor: pointer;
  margin-bottom: 16px;
}

.add-item:active {
  opacity: 0.7;
}

.upload-field-wrap {
  margin-top: 4px;
}

.upload-field {
  height: auto;
  padding: 0;
  background: transparent;
}

.upload-field>>>.van-uploader__upload {
  width: 72px;
  height: 72px;
  margin: 0 8px 8px 0;
  background: #f7f8fa;
  border-radius: 8px;
}

.upload-field>>>.van-uploader__preview {
  margin: 0 8px 8px 0;
}

.upload-field>>>.van-uploader__preview-image {
  border-radius: 8px;
}

.bottom-bar {
  position: fixed;
  right: 0;
  bottom: 0;
  left: 0;
  padding: 12px 20px calc(12px + env(safe-area-inset-bottom));
  background: #ffffff;
  z-index: 11;
}

.confirm-btn {
  width: 100%;
  height: 46px;
  background: linear-gradient(90deg, #ffcb63 0%, #f9ad30 100%);
  border: 0;
  border-radius: 23px;
  font-family: inherit;
  font-size: 16px;
  font-weight: 500;
  color: #ffffff;
  cursor: pointer;
}

.confirm-btn:disabled {
  opacity: 0.6;
}
</style>
